import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import api, { setToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const hash = (window.location.hash || "").replace(/^#/, "");
    const sid = new URLSearchParams(hash).get("session_id") || new URLSearchParams(window.location.search).get("session_id");
    // Strip the temporary session id from the URL immediately.
    window.history.replaceState({}, document.title, "/auth/callback");

    if (!sid) {
      setError("Missing sign-in session. Please try signing in again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        setToken(data.token);
        await refresh();
        navigate(data.user.role === "admin" ? "/admin" : "/app", { replace: true });
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Google sign-in failed. Please try again.");
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-5">
      <div className="w-full max-w-sm text-center">
        {!error ? (
          <div data-testid="auth-callback-loading" className="text-white">
            <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4 text-blue-400" />
            <p className="text-slate-300">Signing you in…</p>
          </div>
        ) : (
          <div data-testid="auth-callback-error" className="bg-white rounded-2xl p-6 shadow-2xl space-y-4">
            <div className="mx-auto h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <p className="text-sm text-slate-600">{error}</p>
            <Button data-testid="callback-back-to-login" onClick={() => navigate("/login", { replace: true })} className="w-full h-11 bg-blue-600 hover:bg-blue-700">
              Back to Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
